import React from 'react';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	Cell,
	LabelList,
} from 'recharts';

interface PriceData {
	maturity: string;
	cee_classique: number | null;
	cee_precarite: number | null;
}

interface ChartData {
	month: string;
	month_label: string;
	prices: PriceData[];
}

interface CeePriceChartProps {
	data: ChartData;
}

const COLORS = {
	classique: '#000091', // Bleu
	precarite: '#e1000f', // Orange
};

const formatPrice = (value: number | null): string => {
	if (value === null) return '';
	return `${value.toFixed(2).replace('.', ',')} €`;
};

const CustomLabel = (props: any) => {
	const { x, y, width, value, fill } = props;
	if (value === null || value === undefined) return null;

	return (
		<text
			x={x + width / 2}
			y={y - 8}
			fill={fill}
			textAnchor="middle"
			fontSize={12}
			fontWeight={600}
		>
			{formatPrice(value)}
		</text>
	);
};

const CeePriceChart: React.FC<CeePriceChartProps> = ({ data }) => {
	if (!data || !data.prices || data.prices.length === 0) {
		return (
			<div className="gext-cee-price-chart gext-cee-price-chart--empty">
				<p>Aucune donnée disponible</p>
			</div>
		);
	}

	// Prépare les données pour le graphique
	const chartData = data.prices.map((item) => ({
		name: item.maturity,
		classique: item.cee_classique,
		precarite: item.cee_precarite,
	}));

	return (
		<div className="gext-cee-price-chart">
			<h3 className="gext-cee-price-chart__title">
				Indices de prix : {data.month_label} (en €/MWhc)
			</h3>

			<ResponsiveContainer width="100%" height={400}>
				<BarChart
					data={chartData}
					margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
					barGap={8}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
					<XAxis
						dataKey="name"
						tick={{ fill: '#6b7280', fontSize: 12 }}
						tickLine={{ stroke: '#d1d5db' }}
						axisLine={{ stroke: '#d1d5db' }}
						label={{
							value: 'Livraisons',
							position: 'bottom',
							offset: 0,
							fill: '#6b7280',
							fontSize: 13,
						}}
					/>
					<YAxis
						tick={{ fill: '#6b7280', fontSize: 12 }}
						tickLine={{ stroke: '#d1d5db' }}
						axisLine={{ stroke: '#d1d5db' }}
						tickFormatter={(value) => `${value.toFixed(1)} €`}
						domain={[0, 'auto']}
					/>
					<Tooltip
						formatter={(value: number) => [
							formatPrice(value),
							'Prix',
						]}
						contentStyle={{
							backgroundColor: '#fff',
							border: '1px solid #e5e7eb',
							borderRadius: '8px',
							boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
						}}
					/>
					<Legend
						verticalAlign="top"
						height={56}
						formatter={(value) =>
							value === 'classique'
								? 'Prix CEE Classique'
								: 'Prix CEE Précarité'
						}
					/>
					<Bar
						dataKey="classique"
						fill={COLORS.classique}
						radius={[4, 4, 0, 0]}
						maxBarSize={50}
					>
						<LabelList
							dataKey="classique"
							content={<CustomLabel fill={COLORS.classique} />}
						/>
					</Bar>
					<Bar
						dataKey="precarite"
						fill={COLORS.precarite}
						radius={[4, 4, 0, 0]}
						maxBarSize={50}
					>
						<LabelList
							dataKey="precarite"
							content={<CustomLabel fill={COLORS.precarite} />}
						/>
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};

export default CeePriceChart;
